import React from "react";
import { useDelete, usePatch, usePost } from "./useCall";
import useQuery, { useMutate } from "./useQuery";
import { comparatorBy } from "diagnostics-lib/comparator";
const useAllTypesProjectCategories = (projectId: string) => {
  const { body: data } = useQuery("/projects/:projectId(uuid)/categories", {
    keepFresh: true,
    routeParams: { projectId },
  });

  const sortedData = React.useMemo(
    () =>
      Object.entries(data).reduce(
        (acc, [key, value]) => ({
          ...acc,
          [key]: value.sort(comparatorBy("name")),
        }),
        {} as typeof data,
      ),
    [data],
  );

  return sortedData;
};

export type ProjectCategoryType = keyof ReturnType<typeof useAllTypesProjectCategories>;
export type ProjectCategory = ReturnType<
  typeof useAllTypesProjectCategories
>[ProjectCategoryType][number];

export const useProjectCategories = (projectId: string, type: ProjectCategoryType) =>
  useAllTypesProjectCategories(projectId)[type];

export const useUpdateProjectCategory = (projectId: string, type: ProjectCategoryType) => {
  const mutate = useMutate("/projects/:projectId(uuid)/categories", {
    routeParams: { projectId },
  });
  const patch = usePatch(
    "/projects/:projectId(uuid)/categories/:type(categoryProjectType)/:id(uuid)",
  );

  return React.useCallback(
    async (id: string, newName: string) => {
      const value = await patch({ routeParams: { type, projectId, id }, body: { newName } });
      mutate(({ body, ...rest }) => ({
        ...rest,
        body: {
          ...body,
          [type]: body[type]
            .map(category =>
              category.id === id ? { ...category, name: value.body.name } : category,
            )
            .sort(comparatorBy("name")),
        },
      }));

      return value;
    },
    [mutate, patch, projectId, type],
  );
};

export const useDeleteProjectCategory = (projectId: string, type: ProjectCategoryType) => {
  const mutate = useMutate("/projects/:projectId(uuid)/categories", {
    routeParams: { projectId },
  });
  const del = useDelete(
    "/projects/:projectId(uuid)/categories/:type(categoryProjectType)/:id(uuid)",
  );

  return React.useCallback(
    async (id: string) => {
      await del({ routeParams: { type, projectId, id } });

      mutate(({ body, ...rest }) => ({
        ...rest,
        body: {
          ...body,
          [type]: body[type].filter(category => category.id !== id),
        },
      }));
    },
    [del, mutate, projectId, type],
  );
};

export const useCreateProjectCategory = (projectId: string, type: ProjectCategoryType) => {
  const mutate = useMutate("/projects/:projectId(uuid)/categories", {
    routeParams: { projectId },
  });
  const post = usePost("/projects/:projectId(uuid)/categories/:type(categoryProjectType)");

  return React.useCallback(
    async (name: string) => {
      const value = await post({ routeParams: { type, projectId }, body: { name } });
      mutate(({ body, ...rest }) => ({
        ...rest,
        body: {
          ...body,
          [type]: [...body[type], { id: value.body.id, name: value.body.name }].sort(
            comparatorBy("name"),
          ),
        },
      }));

      return value;
    },
    [mutate, post, projectId, type],
  );
};
